"use client";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Container,
  Fade,
  Link
} from "@mui/material";
import { styled, alpha } from "@mui/system";
import { redirect, useRouter } from "next/navigation";
import { InputHTMLAttributes, useContext, useEffect, useState } from "react";
import { sha256Hash } from "src/lib/back";
import { UserContext } from "src/contexts/UserContext";
import { IDEContext } from "../contexts/IDEContext";
import Image from "next/image";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Copyright from "src/theme/Copyright";
// Styled components with enhanced design
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(0),
  // backgroundColor: alpha(theme.palette.background.paper, 0.8),
  // backdropFilter: 'blur(10px)',
  borderRadius: 0,
  // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  // transition: 'box-shadow 0.2s ease-in-out',
  overflow: 'hidden',
}));

const HeaderImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  height: 'clamp(80px, 15vw, 120px)',
  marginBottom: theme.spacing(3),
  padding: '0 24px',
  '& > span': {
    position: 'unset !important',
  },
  '& img': {
    objectFit: 'contain !important',
    position: 'relative !important',
    height: '100% !important',
    width: '100% !important',
  },
}));

interface StyledInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

const StyledInput = styled('input', {
  shouldForwardProp: (prop) => prop !== 'error'
})<StyledInputProps>(({ theme, error }) => ({
  width: '100%',
  padding: '12px',
  paddingLeft: '40px',
  height: '48px',
  backgroundColor: `${theme.palette.background.paper} !important`,
  color: theme.palette.text.primary,
  border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 0,
  fontSize: '16px',
  transition: 'all 0.2s ease-in-out',
  '&::placeholder': {
    color: theme.palette.text.secondary,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&:disabled': {
    backgroundColor: alpha(theme.palette.action.disabled, 0.1),
    borderColor: theme.palette.action.disabled,
  },

  // "&:-webkit-autofill": {
  //   backgroundColor: `${theme.palette.background.paper} !important`, // Force background
  //   WebkitBoxShadow: `0 0 0px 1000px ${theme.palette.background.paper} inset !important`, // Remove autofill glow
  //   color: `${theme.palette.text.primary} !important`,
  // },
  // "&:-webkit-autofill::first-line": {
  //   color: `${theme.palette.text.primary} !important`, // Ensure correct text color
  // },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  height: '48px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 0,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const InputIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: theme.palette.text.secondary,
  zIndex: 1,
}));

const StyledFooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '16px', // Increased from 14px for better readability
  padding: theme.spacing(3), // Increased padding for larger touch target
  display: 'block',
  textAlign: 'center',
  transition: 'all 0.2s ease-in-out', // Changed to all for smoother transitions
  cursor: 'pointer',
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, // Add subtle separator
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.4),
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  '&:active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1), // Feedback on touch
  },
  // Mobile-specific enhancements
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px', // Even larger on mobile
    padding: theme.spacing(4), // Larger touch target on mobile
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    // Optional: add subtle visual indicator
    '&::after': {
      content: '""',
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderTop: `2px solid ${theme.palette.text.secondary}`,
      borderRight: `2px solid ${theme.palette.text.secondary}`,
      transform: 'rotate(45deg)',
      marginLeft: theme.spacing(1),
      verticalAlign: 'middle',
      transition: 'transform 0.2s ease-in-out',
    },
    '&:hover::after': {
      transform: 'rotate(45deg) translate(2px, -2px)',
      borderColor: theme.palette.primary.main,
    }
  }
}));

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const { setUser, user } = useContext(UserContext);
  const { loading, setLoading } = useContext(IDEContext);
  const router = useRouter();

  const handleGoSpec = () => {
    if (router) router.push("/docs");
  };
  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    
    if (validateEmail(email)) {
      try {
        const response = await fetch("/api/login", {
          method: "POST",
          body: JSON.stringify({ email, password: sha256Hash(password) }),
        });
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        if (data.email) {
          setUser({ isLoggedIn: true, username: data.email });
          setShowSnackbar(true);
        }
      } catch (err: any) {
        setErrorMsg(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      setEmailError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isLoggedIn) redirect("/");
  }, [user]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Fade in timeout={800}>
          <StyledPaper elevation={0}>
            <HeaderImageContainer>
              <Image
                src="/logo.png"
                alt="Login Header"
                fill={false}
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: '100%', height: '100%' }}
                priority
              />
            </HeaderImageContainer>
 {/* Add the footer link */}
 <StyledFooterLink onClick={handleGoSpec}>
                Language Specification
              </StyledFooterLink>
            <Stack component="form" onSubmit={handleSubmit} spacing={0.5} sx={{ px: 8, pb: 0 }}>
              <Box position="relative">
                <InputIcon>
                  <EmailOutlinedIcon />
                </InputIcon>
                <StyledInput
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(false);
                  }}
                  placeholder="Email address"
                  disabled={loading}
                  required
                  error={emailError}
                  sx={{
                    // "& input:-webkit-autofill": {
                    //   backgroundColor: "#121212 !important",
                    // },
                  }}
                />
                {emailError && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Please enter a valid email address
                  </Typography>
                )}
              </Box>

              <Box position="relative">
                <InputIcon>
                  <LockOutlinedIcon />
                </InputIcon>
                <StyledInput
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={loading}
                  required
                />
                {password && (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}
              </Box>

              {errorMsg && (
                <Alert severity="error" variant="outlined">
                  {errorMsg}
                </Alert>
              )}

              <Stack spacing={2}>
                <StyledButton
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} color="inherit" />}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </StyledButton>

                <StyledButton
                  LinkComponent="a"
                  href="/register"
                  variant="outlined"
                  disabled={loading}
                >
                  Create an account
                </StyledButton>
              </Stack>
            </Stack>
            
              {/* Add spacing before copyright */}
              <Box sx={{ mt: 4, mb: 2 }}>
              <Copyright />
            </Box>
          </StyledPaper>
        </Fade>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled">
            Successfully logged in!
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default LoginScreen;